import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { submit as submitForm, formValueSelector } from 'redux-form';
import { setError, clearError } from '@/actions';
import {
  selectPaymentOption,
  selectPaymentMethod,
  selectSeats,
  updatePurchaseFields,
  fetchYunoData,
  toggleWantsCarbonOffset,
} from '@/actions/purchase';
import {
  cardPaymentAttempt,
  transferPaymentAttempt,
  evertecPaymentAttempt,
  confirmEvertecPayment,
  storePaymentAttempt,
  yunoPaymentAttempt,
  walletPaymentAttempt,
  efectyPaymentAttempt,
  bnplPaymentAttempt,
  yapePaymentAttempt,
} from '@/actions/payment';
import completeQuickPurchase, {
  updateQuickPurchaseInsurance,
  updateQuickPurchasePassengerInsurance,
  updateQuickPurchaseSeats,
} from '@/actions/quickPurchase';
import { paypalPaymentAttempt } from '@/actions/paypal';
import { getBrand2ProcessorMap } from 'utils/documentMaps';
import { dispatchClearSelectedSeats } from '../../features/purchase/redux/dispatchers';

const purchaserSelector = formValueSelector('purchaser');

/**
 * useQuickCheckoutActions hook.
 * Wrap Redux dispatch actions for QuickCheckout payment operations.
 * Replaces mapDispatchToProps and mergeProps from QuickCheckoutContainer.
 * @param {object} params - history.
 * @param {object} params.history - React Router history object.
 * @returns {object} QuickCheckout action callbacks.
 */
function useQuickCheckoutActions({ history }) {
  const dispatch = useDispatch();
  const purchaseState = useSelector((state) => state.purchase.toJS());
  const { monthlySelectedPlan } = purchaseState;
  const env = useSelector((state) => state.whitelabelConfig.env);
  const purchaserFormValues = useSelector(
    (state) => purchaserSelector(state, 'documentId', 'documentType', 'email'),
    // eslint-disable-next-line function-paren-newline
  );

  const onSubmitForm = useCallback((formName) => dispatch(submitForm(formName)), [dispatch]);

  const onFormFail = useCallback(
    () =>
      dispatch(
        setError(null, 'check_the_fields_in_red', 'warning', false, null, null, null, 'fixed'),
      ),
    [dispatch],
  );

  const onNoCardSelected = useCallback(
    () => dispatch(setError(200, 'select_a_card', 'warning', false, null, null, null, 'fixed')),
    [dispatch],
  );

  const onSelectPaymentOption = useCallback(
    (option, method) => {
      dispatch(selectPaymentOption(option));
      dispatch(selectPaymentMethod(method || {}));
    },
    [dispatch],
  );

  const onTransferPaymentAttempt = useCallback(
    (transferData) => {
      const {
        token,
        documentId: purchaseDocumentId,
        documentType: purchaseDocumentType,
        email: purchaseEmail,
        description,
        total,
        selectedInstallmentsPlan,
        selectedPaymentMethod,
        phone,
        phoneCode,
        phoneCountry,
      } = purchaseState;
      const documentId = purchaserFormValues?.documentId || purchaseDocumentId;
      const documentType = purchaserFormValues?.documentType || purchaseDocumentType;
      const email = purchaserFormValues?.email || purchaseEmail;
      const { engine, provider } = selectedPaymentMethod || {};
      const isPSEMercadoPago = engine === 'mercadopago' && provider === 'pse';

      const validatedTotal =
        selectedInstallmentsPlan && selectedInstallmentsPlan.months > 1
          ? selectedInstallmentsPlan.total
          : total;

      const brand2ProcessorMap = getBrand2ProcessorMap();
      const processorDocType = brand2ProcessorMap[documentType];

      dispatch(
        transferPaymentAttempt({
          bankId: transferData.bank,
          personType: transferData.personType,
          documentType: isPSEMercadoPago ? processorDocType : documentType,
          documentNumber: documentId,
          email,
          paymentDescription: description,
          purchaseToken: token,
          total: validatedTotal,
          engine,
          provider,
          addressAttributes: isPSEMercadoPago
            ? {
                ...transferData.addressAttributes,
                phone,
                phoneCode,
                phoneCountry,
              }
            : {},
        }),
      );
    },
    [dispatch, purchaseState, purchaserFormValues],
  );

  const onEvertecPaymentAttempt = useCallback(
    (paymentData) => dispatch(evertecPaymentAttempt(paymentData)),
    [dispatch],
  );

  const onConfirmEvertecPayment = useCallback(() => dispatch(confirmEvertecPayment()), [dispatch]);

  const onEvertecPaymentFailure = useCallback(
    () => dispatch(setError(301, 'error_generating_payment', 'warning', false)),
    [dispatch],
  );

  // Last resort for a payment attempt that failed without telling the buyer (PRO2-147). The
  // notification is cleared at the start of every attempt, so one showing now means the failure
  // path already said something specific (expired purchase, unavailable schedule, a decline), and
  // that message must not be replaced with the generic one. The message is checked as well as the
  // code because some warnings carry a null code (`onFormFail`'s "check the fields in red").
  const onPaymentFailure = useCallback(
    () =>
      dispatch((innerDispatch, getState) => {
        const { code, message } = getState().errors || {};
        if (code || message) return;
        innerDispatch(setError(301, 'error_generating_payment', 'warning', false));
      }),
    [dispatch],
  );

  const onCardPaymentAttempt = useCallback(
    (token, cardValues, paymentInfo) => {
      const cardValueWithMonthlyPlan = {
        ...cardValues,
        monthlySelectedPlan,
      };
      dispatch(cardPaymentAttempt(token, cardValueWithMonthlyPlan, paymentInfo));
    },
    [dispatch, monthlySelectedPlan],
  );

  const onClearError = useCallback(() => dispatch(clearError()), [dispatch]);

  const transitionTo = useCallback((route) => history.push(route), [history]);

  const onCompleteQuickPurchase = useCallback(
    (params) => dispatch(completeQuickPurchase(params)),
    [dispatch],
  );

  const onToggleWantsInsurance = useCallback(
    (token, wants) => dispatch(updateQuickPurchaseInsurance(token, wants)),
    [dispatch],
  );

  const onTogglePassengerInsurance = useCallback(
    (token, wants) => dispatch(updateQuickPurchasePassengerInsurance(token, wants)),
    [dispatch],
  );

  const onSetPurchaser = useCallback(
    (token, fields) => dispatch(updatePurchaseFields(token, fields)),
    [dispatch],
  );

  const onStorePaymentAttempt = useCallback(() => {
    const {
      token,
      purchaserFirstName,
      purchaserLastName,
      documentId,
      documentType,
      selectedInstallmentsPlan,
      description,
      total,
    } = purchaseState;

    const validatedTotal =
      selectedInstallmentsPlan && selectedInstallmentsPlan.months > 1
        ? selectedInstallmentsPlan.total
        : total;

    const brand2ProcessorMap = getBrand2ProcessorMap();
    dispatch(
      storePaymentAttempt({
        token,
        documentType: brand2ProcessorMap[documentType],
        name: purchaserFirstName,
        lastName: purchaserLastName,
        identification: documentId,
        totalAmount: validatedTotal,
        currency: env.currency,
        description,
      }),
    );
  }, [dispatch, purchaseState, env.currency]);

  const onYunoPaymentAttempt = useCallback(
    (config) => dispatch(yunoPaymentAttempt(config)),
    [dispatch],
  );

  const onWalletPaymentAttempt = useCallback(() => dispatch(walletPaymentAttempt()), [dispatch]);

  const onEfectyPaymentAttempt = useCallback(() => dispatch(efectyPaymentAttempt()), [dispatch]);

  const onYapePaymentAttempt = useCallback(
    (yapeFormData) => dispatch(yapePaymentAttempt(yapeFormData)),
    [dispatch],
  );

  const onPaypalPaymentAttempt = useCallback(
    (paypalType, paypalOptions) => dispatch(paypalPaymentAttempt(paypalType, paypalOptions)),
    [dispatch],
  );

  const onBnplPaymentAttempt = useCallback(() => dispatch(bnplPaymentAttempt()), [dispatch]);

  const onFetchYunoData = useCallback((params) => dispatch(fetchYunoData(params)), [dispatch]);

  const onToggleWantsCarbonOffset = useCallback(
    (token, wants) => dispatch(toggleWantsCarbonOffset(token, wants)),
    [dispatch],
  );

  const onClearSelectedSeats = useCallback(
    () => dispatch(dispatchClearSelectedSeats()),
    [dispatch],
  );

  const onUpdateQuickPurchaseSeats = useCallback(
    (token, seats, options) => dispatch(updateQuickPurchaseSeats(token, seats, options)),
    [dispatch],
  );

  const onSelectSeats = useCallback((way, seats) => dispatch(selectSeats(way, seats)), [dispatch]);

  return {
    onSubmitForm,
    onFormFail,
    onNoCardSelected,
    onSelectPaymentOption,
    onTransferPaymentAttempt,
    onEvertecPaymentAttempt,
    onConfirmEvertecPayment,
    onEvertecPaymentFailure,
    onPaymentFailure,
    onCardPaymentAttempt,
    clearError: onClearError,
    transitionTo,
    onCompleteQuickPurchase,
    onToggleWantsInsurance,
    onTogglePassengerInsurance,
    onSetPurchaser,
    onStorePaymentAttempt,
    onYunoPaymentAttempt,
    onWalletPaymentAttempt,
    onEfectyPaymentAttempt,
    onYapePaymentAttempt,
    onPaypalPaymentAttempt,
    onBnplPaymentAttempt,
    onFetchYunoData,
    onToggleWantsCarbonOffset,
    onClearSelectedSeats,
    onUpdateQuickPurchaseSeats,
    onSelectSeats,
  };
}

export default useQuickCheckoutActions;
